import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SelectField, StatusPill } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, Technical, When, useAuth, useToast } from "@ozpanel/ui";

// ozpanel releases (O10): what runs, what is published, whether the licence covers it and whether it may
// replace this one, and how releases install — on an admin's action (the default) or automatically (off by
// default). GET/POST/PUT /ozpanel/updates… (internal/modules/releaseupdates). Installing goes through the
// cluster's signed channel: the master installs and restarts, then publishes to every member.

interface Upgrade {
  allowed: boolean;
  kind: string;
  why: string;
}
interface Check {
  running: string;
  build: string;
  licence: { updates_allowed: boolean; state: string; reasons?: string[] | null };
  release?: { version: string; build?: string; notes?: string; released_at?: string; url: string; sha256: string };
  upgrade: Upgrade;
  can_install: boolean;
  why: string;
  error?: string;
}
interface Settings {
  mode: "manual" | "automatic";
  window_start: number;
  window_end: number;
  checked_at?: string;
  available_version?: string;
  last_attempt_at?: string;
  last_ok: boolean;
  last_result?: string;
}
interface UpdatesStatus {
  settings: Settings;
  check: Check;
  note?: string;
}

const hours = Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: `${String(h).padStart(2, "0")}:00 UTC` }));

/** The release-updates module's panel (MODULE_OPS["release-updates"]). */
export function UpdatesPanel() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["ozpanel-updates"],
    queryFn: () => client.apiFetch<UpdatesStatus>("/ozpanel/updates"),
    refetchInterval: 10 * 60_000,
    retry: false,
  });
  const [confirm, setConfirm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const save = async (body: Partial<Settings>) => {
    try {
      await client.apiFetch("/ozpanel/updates/settings", { method: "PUT", body: JSON.stringify(body) });
      void qc.invalidateQueries({ queryKey: ["ozpanel-updates"] });
      notify({ kind: "success", title: "Update settings saved" });
    } catch (e) {
      notify({ kind: "error", title: "Not saved", body: e instanceof Error ? e.message : String(e) });
    }
  };

  const install = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      await client.apiFetch("/ozpanel/updates/install", { method: "POST", body: JSON.stringify({ confirm: true }) });
      notify({
        kind: "success",
        title: "Installing",
        body: "The master installs the release and restarts, then publishes it to every member. This page reconnects in a minute.",
      });
    } catch (e) {
      notify({ kind: "error", title: "Not installed", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
      void qc.invalidateQueries({ queryKey: ["ozpanel-updates"] });
    }
  };

  const d = q.data;
  const c = d?.check;
  const s = d?.settings;
  return (
    <div className="space-y-4">
      <StateView loading={q.isLoading} error={q.error} errorContext="the updates">
        {d && c && s && (
          <>
            <Section
              title="ozpanel release"
              actions={
                c.error ? (
                  <StatusPill tone="neutral">could not check</StatusPill>
                ) : c.can_install ? (
                  <StatusPill tone="warn">update available</StatusPill>
                ) : (
                  <StatusPill tone="ok">{c.upgrade.kind === "current" ? "up to date" : "nothing to install"}</StatusPill>
                )
              }
            >
              <dl className="grid gap-x-6 gap-y-2 text-[13.5px] sm:grid-cols-2">
                <Row label="Running">
                  v{c.running} <span className="text-muted">({c.build})</span>
                </Row>
                <Row label="Published">{c.release ? `${c.release.version}${c.release.build ? ` (${c.release.build})` : ""}` : "—"}</Row>
                <Row label="Licence covers it">{c.licence.updates_allowed ? "yes" : "no"}</Row>
                <Row label="Last checked">{s.checked_at ? <When value={s.checked_at} /> : "not yet"}</Row>
              </dl>
              <p className={`mt-3 text-[13px] ${c.error ? "text-warn" : ""}`}>{c.error ?? c.why}</p>
              {c.release?.notes && (
                <div className="mt-3">
                  <div className="mb-1 text-[12.5px] font-medium text-muted">Release notes</div>
                  <p className="whitespace-pre-line text-[13px]">{c.release.notes}</p>
                </div>
              )}
              <div className="mt-3">
                <Button variant="accent" size="sm" disabled={!c.can_install || busy} onClick={() => setConfirm(true)}>
                  {busy ? "Installing…" : c.release ? `Install ${c.release.version}` : "Install"}
                </Button>
              </div>
              {s.last_attempt_at && (
                <p className={`mt-3 text-[12.5px] ${s.last_ok ? "text-muted" : "text-warn"}`}>
                  Last install <When value={s.last_attempt_at} />: {s.last_result}
                </p>
              )}
              <Technical>
                <div className="mt-3 space-y-1 font-mono text-[11.5px] text-faint">
                  {c.release && <div>sha256 {c.release.sha256}</div>}
                  {c.release && <div>{c.release.url}</div>}
                  <div>
                    upgrade: {c.upgrade.kind} — {c.upgrade.why}
                  </div>
                  {(c.licence.reasons ?? []).map((r) => (
                    <div key={r}>{r}</div>
                  ))}
                </div>
              </Technical>
            </Section>

            <Section title="How releases install">
              <div className="grid gap-3 sm:grid-cols-3">
                <SelectField
                  label="Mode"
                  value={s.mode}
                  onChange={(mode) => void save({ mode: mode as Settings["mode"] })}
                  options={[
                    { value: "manual", label: "When an admin presses Install" },
                    { value: "automatic", label: "Automatically" },
                  ]}
                />
                <SelectField
                  label="Automatic installs from"
                  value={String(s.window_start)}
                  onChange={(v) => void save({ window_start: Number(v) })}
                  options={hours}
                />
                <SelectField
                  label="until"
                  value={String(s.window_end)}
                  onChange={(v) => void save({ window_end: Number(v) })}
                  options={hours}
                />
              </div>
              <p className="mt-3 text-[12.5px] text-muted">
                Automatic is off unless switched on here. Either way a release is installed only when the licence covers it,
                both of its publisher signatures verify, and it is newer than what runs — a downgrade is never installed.
                The master installs first and restarts; then every member takes it, one at a time.
              </p>
              {d.note && <p className="mt-2 text-[12.5px] text-warn">{d.note}</p>}
            </Section>

            <ConfirmDialog
              open={confirm}
              onOpenChange={setConfirm}
              title={`Install ${c.release?.version ?? "the release"}?`}
              confirmLabel="Install"
              destructive={false}
              body={
                <p>
                  The master installs it and restarts (the panel is away for under a minute), then each member takes it in
                  turn. Sites, mail and apps keep running; each service restarts briefly as its node takes the release.
                </p>
              }
              onConfirm={() => void install()}
            />
          </>
        )}
      </StateView>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
